import { create } from "zustand";
import { getPageSessionID } from "../services/clientSession";

const STORAGE_KEY = `iot-debugger.session.v3.${getPageSessionID()}`;

export type CredentialHistoryItem = {
  app_id: string;
  app_secret: string;
  last_used_at: string;
};

type SessionState = {
  appId: string;
  appSecret: string;
  token: string | null;
  expiresAt: Date | null;
  credentialHistory: CredentialHistoryItem[];
  setCredentials: (appId: string, appSecret: string) => void;
  useCredential: (item: CredentialHistoryItem) => void;
  rememberCredential: (appId: string, appSecret: string) => void;
  clearCredentialHistory: () => void;
  setToken: (token: string, expiresAt: string | Date) => void;
  clear: () => void;
};

type PersistedSession = {
  appId: string;
  appSecret: string;
  token: string | null;
  expiresAt: string | null;
  credentialHistory: CredentialHistoryItem[];
};

const readPersistedState = (): PersistedSession => {
  const fallback: PersistedSession = {
    appId: "",
    appSecret: "",
    token: null,
    expiresAt: null,
    credentialHistory: []
  };
  if (typeof window === "undefined") {
    return fallback;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    return {
      appId: parsed.appId ?? "",
      appSecret: parsed.appSecret ?? "",
      token: parsed.token ?? null,
      expiresAt: parsed.expiresAt ?? null,
      credentialHistory: Array.isArray(parsed.credentialHistory)
        ? parsed.credentialHistory
            .filter(
              (entry): entry is CredentialHistoryItem =>
                typeof entry?.app_id === "string" &&
                typeof entry?.app_secret === "string"
            )
            .slice(0, 8)
        : []
    };
  } catch {
    return fallback;
  }
};

const persistState = (state: SessionState) => {
  if (typeof window === "undefined") {
    return;
  }
  const payload: PersistedSession = {
    appId: state.appId,
    appSecret: state.appSecret,
    token: state.token,
    expiresAt: state.expiresAt ? state.expiresAt.toISOString() : null,
    credentialHistory: state.credentialHistory
  };
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

const persisted = readPersistedState();

const useSessionStore = create<SessionState>((set) => ({
  appId: persisted.appId,
  appSecret: persisted.appSecret,
  token: persisted.token,
  expiresAt: persisted.expiresAt ? new Date(persisted.expiresAt) : null,
  credentialHistory: persisted.credentialHistory,
  setCredentials: (appId, appSecret) =>
    set((state) => {
      const next = {
        appId,
        appSecret
      };
      persistState({
        ...state,
        ...next
      });
      return next;
    }),
  useCredential: (item) =>
    set((state) => {
      const next = {
        appId: item.app_id,
        appSecret: item.app_secret
      };
      persistState({
        ...state,
        ...next
      });
      return next;
    }),
  rememberCredential: (appId, appSecret) =>
    set((state) => {
      const normalizedAppId = appId.trim();
      const normalizedSecret = appSecret.trim();
      if (!normalizedAppId || !normalizedSecret) {
        return state;
      }
      const deduped = state.credentialHistory.filter(
        (item) =>
          !(
            item.app_id === normalizedAppId &&
            item.app_secret === normalizedSecret
          )
      );
      const nextHistory: CredentialHistoryItem[] = [
        {
          app_id: normalizedAppId,
          app_secret: normalizedSecret,
          last_used_at: new Date().toISOString()
        },
        ...deduped
      ].slice(0, 8);
      persistState({
        ...state,
        credentialHistory: nextHistory
      });
      return {
        credentialHistory: nextHistory
      };
    }),
  clearCredentialHistory: () =>
    set((state) => {
      persistState({
        ...state,
        credentialHistory: []
      });
      return {
        credentialHistory: []
      };
    }),
  setToken: (token, expires) =>
    set((state) => {
      const next = {
        token,
        expiresAt: expires ? new Date(expires) : null
      };
      persistState({
        ...state,
        ...next
      });
      return next;
    }),
  clear: () =>
    set((state) => {
      const next = {
        token: null,
        expiresAt: null
      };
      persistState({
        ...state,
        ...next
      });
      return next;
    })
}));

export default useSessionStore;
